import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../../components/AuthContext';
import TileSelectionModal from '../../components/TileSelectionModal';
import Swal from 'sweetalert2';
import { FaPlus, FaTrash, FaTimes, FaCalendarAlt, FaBox, FaEye, FaFilter, FaDownload, FaExchangeAlt, FaArrowRight, FaMapMarkerAlt, FaStickyNote, FaEdit, FaFileExport, FaHistory, FaChevronLeft, FaChevronRight, FaSave } from 'react-icons/fa';
import Select from 'react-select';

const TilesLocationTransfer = () => {
  const { user } = useContext(AuthContext);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tiles, setTiles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedFromLocation, setSelectedFromLocation] = useState(null);
  const [selectedToLocation, setSelectedToLocation] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [remarks, setRemarks] = useState("");
  
  // Item state
  const [items, setItems] = useState([{
    tileId: null,
    tileName: '',
    tileDetails: null,
    freshStockBox: 0,
    freshStockPieces: 0,
    cornerDamagedBox: 0,
    cornerDamagedPieces: 0,
    fullDamagedBox: 0,
    fullDamagedPieces: 0,
    availableStock: { freshStockBox: 0, freshStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0, fullDamagedBox: 0, fullDamagedPieces: 0 }
  }]);

  // Tile selection modal state
  const [showTilesModal, setShowTilesModal] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(null);
  
  // Transfer history state
  const [transferHistory, setTransferHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit, setHistoryLimit] = useState(10);
  const [totalHistory, setTotalHistory] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [historyFilters, setHistoryFilters] = useState({
    fromLocation: null,
    toLocation: null,
    startDate: null,
    endDate: null,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('entry');
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [viewMode, setViewMode] = useState('grid');

  // Refs for focus management
  const freshStockBoxRefs = useRef([]);
  const freshStockPiecesRefs = useRef([]);
  const cornerDamagedBoxRefs = useRef([]);
  const cornerDamagedPiecesRefs = useRef([]);
  const fullDamagedBoxRefs = useRef([]);
  const fullDamagedPiecesRefs = useRef([]);
  const filterDateStartRef = useRef(null);
  const filterDateEndRef = useRef(null);

  // Sync dark mode with localStorage
  useEffect(() => {
    const interval = setInterval(() => {
      const stored = localStorage.getItem('darkMode') === 'true';
      if (stored !== darkMode) setDarkMode(stored);
    }, 500);
    return () => clearInterval(interval);
  }, [darkMode]);

  // Initial data loading
  useEffect(() => {
    fetchTiles();
    fetchLocations();
    fetchTransferHistory();
  }, []);
  
  // Load transfer history when filters or pagination changes
  useEffect(() => {
    fetchTransferHistory();
  }, [historyPage, historyLimit, historyFilters]);

  // Update refs when items change
  useEffect(() => {
    freshStockBoxRefs.current = freshStockBoxRefs.current.slice(0, items.length);
    freshStockPiecesRefs.current = freshStockPiecesRefs.current.slice(0, items.length);
    cornerDamagedBoxRefs.current = cornerDamagedBoxRefs.current.slice(0, items.length);
    cornerDamagedPiecesRefs.current = cornerDamagedPiecesRefs.current.slice(0, items.length);
    fullDamagedBoxRefs.current = fullDamagedBoxRefs.current.slice(0, items.length);
    fullDamagedPiecesRefs.current = fullDamagedPiecesRefs.current.slice(0, items.length);
  }, [items]);

  const fetchOpeningStock = async (tileId, locationId, date) => {
    try {
      const params = new URLSearchParams({
        startDate: date,
        tileId: tileId,
        location: locationId,
        limit: '1'
      });

      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/tiles/opening-stock?${params}`);
      
      if (response.data.success && response.data.data.tiles.length > 0) {
        const tileData = response.data.data.tiles[0];
        if (tileData.openingStockByLocation.length > 0) {
          return tileData.openingStockByLocation[0];
        }
      }
      return null;
    } catch (error) {
      console.error('Error fetching opening stock:', error);
      return null;
    }
  };

  const fetchTiles = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tiles?userId=${user.user._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTiles(Array.isArray(data) ? data : data.data || []);
      }
    } catch (error) {
      console.error('Error fetching tiles:', error);
      Swal.fire({
        customClass: { popup: 'swal-dark-mode' },
        title: 'Error',
        text: 'Failed to load tiles',
        icon: 'error'
      });
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/locations/companyId/${user.user.company._id}`);
      const data = await response.json();
      setLocations(Array.isArray(data) ? data : data.data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      Swal.fire({
        customClass: { popup: 'swal-dark-mode' },
        title: 'Error',
        text: 'Failed to load locations',
        icon: 'error'
      });
    }
  };
  
  const fetchTransferHistory = async () => {
    try {
      setHistoryLoading(true);
      const queryParams = new URLSearchParams({
        page: historyPage.toString(),
        limit: historyLimit.toString(),
        userId: user.user._id,
        ...(historyFilters.fromLocation && { fromLocation: historyFilters.fromLocation }),
        ...(historyFilters.toLocation && { toLocation: historyFilters.toLocation }),
        ...(historyFilters.startDate && { startDate: historyFilters.startDate }),
        ...(historyFilters.endDate && { endDate: historyFilters.endDate })
      });

      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/tiles/location-transfers?${queryParams}`);
      const data = response.data;

      if (data.success) {
        const transfersData = data.data?.transfers || data.transfers || [];
        console.log('Transfer history data (raw):', transfersData);
        
        // Ensure location data is properly formatted
        const processedTransfers = transfersData.map(transfer => {
          return {
            ...transfer,
            fromLocation: transfer.fromLocation || {},
            toLocation: transfer.toLocation || {},
            // Add fallbacks for location names
            fromLocationName: transfer.fromLocationName || 
              transfer.fromLocation?.locationName || 
              transfer.fromLocation?.name || 
              "Unknown",
            toLocationName: transfer.toLocationName || 
              transfer.toLocation?.locationName || 
              transfer.toLocation?.name || 
              "Unknown"
          };
        });
        
        setTransferHistory(processedTransfers);
        setTotalHistory(data.total || data.data?.total || 0);
        // Set total pages for pagination
        setTotalPages(Math.ceil((data.total || data.data?.total || 0) / historyLimit));
        console.log('Transfer history data (processed):', processedTransfers);
      }
    } catch (error) {
      console.error('Error fetching transfer history:', error);
      Swal.fire({
        customClass: { popup: 'swal-dark-mode' },
        title: 'Error',
        text: 'Failed to load transfer history',
        icon: 'error'
      });
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleTileSelect = async (tile) => {
    if (currentItemIndex !== null && selectedFromLocation) {
      // Get available stock for this tile at selected from location using current stock data
      const locationStock = tile.stock?.find(s => {
        const stockLocationId = s.location?._id || s.location;
        return stockLocationId === selectedFromLocation.value || stockLocationId?.toString() === selectedFromLocation.value?.toString();
      }) || {
        freshStockBox: 0, freshStockPieces: 0,
        cornerDamagedBox: 0, cornerDamagedPieces: 0,
        fullDamagedBox: 0, fullDamagedPieces: 0
      };

      const updatedItems = [...items];
      updatedItems[currentItemIndex] = {
        ...updatedItems[currentItemIndex],
        tileId: tile._id,
        tileName: tile.tilesName,
        tileDetails: tile,
        availableStock: {
          freshStockBox: locationStock.freshStockBox || 0,
          freshStockPieces: locationStock.freshStockPieces || 0,
          cornerDamagedBox: locationStock.cornerDamagedBox || 0,
          cornerDamagedPieces: locationStock.cornerDamagedPieces || 0,
          fullDamagedBox: locationStock.fullDamagedBox || 0,
          fullDamagedPieces: locationStock.fullDamagedPieces || 0
        }
      };
      setItems(updatedItems);
      setShowTilesModal(false);
      setCurrentItemIndex(null);

      // Focus on fresh stock box for the selected item
      setTimeout(() => {
        freshStockBoxRefs.current[currentItemIndex]?.focus();
      }, 100);
    } else if (!selectedFromLocation) {
      Swal.fire({
        customClass: { popup: 'swal-dark-mode' },
        title: 'Missing Information',
        text: 'Please select a "From Location" first',
        icon: 'warning'
      });
    }
  };

  const handleStockChange = (index, field, value) => {
    const numValue = Math.max(0, parseInt(value) || 0);
    const item = items[index];
    
    // Check if trying to exceed available stock
    if (item.availableStock) {
      const maxValue = item.availableStock[field] || 0;
      if (numValue > maxValue) {
        Swal.fire({
        customClass: { popup: 'swal-dark-mode' },
          title: 'Stock Limit Exceeded',
          text: `Cannot exceed available stock (${maxValue})`,
          icon: 'warning'
        });
        return;
      }
    }

    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: numValue
    };
    setItems(updatedItems);
  };

  const handleAddItem = () => {
    setItems([...items, {
      tileId: null,
      tileName: '',
      tileDetails: null,
      freshStockBox: 0,
      freshStockPieces: 0,
      cornerDamagedBox: 0,
      cornerDamagedPieces: 0,
      fullDamagedBox: 0,
      fullDamagedPieces: 0,
      availableStock: { freshStockBox: 0, freshStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0, fullDamagedBox: 0, fullDamagedPieces: 0 }
    }]);
  };

  const handleRemoveItem = (index) => {
    if (items.length > 1) {
      const updatedItems = items.filter((_, i) => i !== index);
      setItems(updatedItems);
    }
  };

  // Enhanced keyboard controls
  const handleKeyDown = (e, index, field) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const fieldOrder = ['freshStockBox', 'freshStockPieces', 'cornerDamagedBox', 'cornerDamagedPieces', 'fullDamagedBox', 'fullDamagedPieces'];
      const currentFieldIndex = fieldOrder.indexOf(field);
      
      if (currentFieldIndex < fieldOrder.length - 1) {
        // Move to next field in same row
        const nextField = fieldOrder[currentFieldIndex + 1];
        const refMap = {
          freshStockBox: freshStockBoxRefs,
          freshStockPieces: freshStockPiecesRefs,
          cornerDamagedBox: cornerDamagedBoxRefs,
          cornerDamagedPieces: cornerDamagedPiecesRefs,
          fullDamagedBox: fullDamagedBoxRefs,
          fullDamagedPieces: fullDamagedPiecesRefs
        };
        refMap[nextField]?.current[index]?.focus();
      } else if (index < items.length - 1) {
        // Move to first field of next row
        freshStockBoxRefs.current[index + 1]?.focus();
      } else {
        // Add new item and focus on its first field
        handleAddItem();
        setTimeout(() => {
          freshStockBoxRefs.current[items.length]?.focus();
        }, 100);
      }
    } else if (e.key === 'F2') {
      e.preventDefault();
      openTileSelectionModal(index);
    }
  };

  const validateForm = () => {
    if (!selectedFromLocation) {
      Swal.fire({
        customClass: { popup: 'swal-dark-mode' },
        title: 'Validation Error',
        text: 'Please select a "From Location"',
        icon: 'warning'
      });
      return false;
    }
    if (!selectedToLocation) {
      Swal.fire({
        customClass: { popup: 'swal-dark-mode' },
        title: 'Validation Error',
        text: 'Please select a "To Location"',
        icon: 'warning'
      });
      return false;
    }
    if (selectedFromLocation.value === selectedToLocation.value) {
      Swal.fire({
        customClass: { popup: 'swal-dark-mode' },
        title: 'Validation Error',
        text: 'From and To locations cannot be the same',
        icon: 'warning'
      });
      return false;
    }
    if (!selectedDate) {
      Swal.fire({
        customClass: { popup: 'swal-dark-mode' },
        title: 'Validation Error',
        text: 'Please select a transfer date',
        icon: 'warning'
      });
      return false;
    }

    const validItems = items.filter(item => 
      item.tileId && (item.freshStockBox > 0 || item.freshStockPieces > 0 || item.cornerDamagedBox > 0 || item.cornerDamagedPieces > 0 || item.fullDamagedBox > 0 || item.fullDamagedPieces > 0)
    );

    if (validItems.length === 0) {
      Swal.fire({
        customClass: { popup: 'swal-dark-mode' },
        title: 'Validation Error',
        text: 'Please add at least one item with transfer quantity',
        icon: 'warning'
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setLoading(true);
      
      const validItems = items
        .filter(item => item.tileId && (item.freshStockBox > 0 || item.freshStockPieces > 0 || item.cornerDamagedBox > 0 || item.cornerDamagedPieces > 0 || item.fullDamagedBox > 0 || item.fullDamagedPieces > 0))
        .map(item => ({
          tileId: item.tileId,
          freshStockBox: item.freshStockBox,
          freshStockPieces: item.freshStockPieces,
          cornerDamagedBox: item.cornerDamagedBox,
          cornerDamagedPieces: item.cornerDamagedPieces,
          fullDamagedBox: item.fullDamagedBox,
          fullDamagedPieces: item.fullDamagedPieces
        }));

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tiles/location-transfers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: validItems,
          fromLocation: selectedFromLocation.value,
          toLocation: selectedToLocation.value,
          transferDate: selectedDate instanceof Date ? selectedDate.toISOString().split('T')[0] : selectedDate,
          remarks,
          createdBy: user.user._id
        })
      });

      const result = await response.json();
      
      if (result.success) {
        Swal.fire({
        customClass: { popup: 'swal-dark-mode' },
          title: 'Success!',
          text: 'Location transfer created successfully!',
          icon: 'success',
          confirmButtonText: 'OK'
        });
        
        // Reset form
        setItems([{
          tileId: null,
          tileName: '',
          tileDetails: null,
          freshStockBox: 0,
          freshStockPieces: 0,
          cornerDamagedBox: 0,
          cornerDamagedPieces: 0,
          fullDamagedBox: 0,
          fullDamagedPieces: 0,
          availableStock: { freshStockBox: 0, freshStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0, fullDamagedBox: 0, fullDamagedPieces: 0 }
        }]);
        setSelectedFromLocation(null);
        setSelectedToLocation(null);
        setSelectedDate(new Date());
        setRemarks("");
        
        // Refresh history and tiles
        fetchTransferHistory();
        fetchTiles();
      } else {
        Swal.fire({
        customClass: { popup: 'swal-dark-mode' },
          title: 'Error',
          text: result.message || 'Failed to create location transfer',
          icon: 'error'
        });
      }
    } catch (error) {
      console.error('Error creating location transfer:', error);
      Swal.fire({
        customClass: { popup: 'swal-dark-mode' },
        title: 'Error',
        text: 'Failed to create location transfer',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleHistoryFilterChange = (field, value) => {
    setHistoryFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setHistoryPage(1);
  };

  const resetHistoryFilters = () => {
    setHistoryFilters({
      fromLocation: null,
      toLocation: null,
      startDate: null,
      endDate: null
    });
    setHistoryPage(1);
  };

  // Enhanced filter controls with keyboard navigation
  const handleFilterKeyDown = (e, filterType) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filterType === 'startDate' && filterDateEndRef.current) {
        filterDateEndRef.current.focus();
      } else if (filterType === 'endDate') {
        applyFilters();
      }
    }
  };

  const applyFilters = () => {
    setHistoryPage(1);
    fetchTransferHistory();
  };

  // Open tile selection with enhanced focus
  const openTileSelectionModal = (index) => {
    if (!selectedFromLocation) {
      Swal.fire({
        customClass: { popup: 'swal-dark-mode' },
        title: 'Missing Information',
        text: 'Please select a "From Location" first',
        icon: 'warning'
      });
      return;
    }
    setCurrentItemIndex(index);
    setShowTilesModal(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const calculateTotalStock = (items) => {
    return items.reduce((total, item) => {
      return total + (item.freshStockBox || 0) + (item.cornerDamagedBox || 0) + (item.fullDamagedBox || 0);
    }, 0);
  };

  // Details Modal Component
  const DetailsModal = () => (
    showDetailsModal && selectedTransfer && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className={`rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className={`px-6 py-4 border-b ${darkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
            <div className="flex items-center justify-between">
              <h3 className={`text-xl font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>Transfer Details - {selectedTransfer.transferId}</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className={`transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h4 className={`font-medium mb-3 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>Transfer Information</h4>
                <div className={`space-y-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <div><span className="font-medium">Transfer ID:</span> {selectedTransfer.transferId}</div>
                  <div><span className="font-medium">Date:</span> {formatDate(selectedTransfer.transferDate)}</div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">From:</span> 
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                      {selectedTransfer.fromLocation?.locationName || selectedTransfer.fromLocation?.name || "Unknown"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">To:</span>
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                      {selectedTransfer.toLocation?.locationName || selectedTransfer.toLocation?.name || "Unknown"}
                    </span>
                  </div>
                  <div><span className="font-medium">Created By:</span> {selectedTransfer.createdBy?.firstName} {selectedTransfer.createdBy?.lastName}</div>
                  <div><span className="font-medium">Total Items:</span> {selectedTransfer.items?.length || 0}</div>
                  <div><span className="font-medium">Total Boxes:</span> {calculateTotalStock(selectedTransfer.items || [])}</div>
                </div>
              </div>
              <div>
                <h4 className={`font-medium mb-3 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>Remarks</h4>
                <div className={`p-3 rounded-lg text-sm min-h-[100px] ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-700'}`}>
                  {selectedTransfer.remarks || 'No remarks provided'}
                </div>
              </div>
            </div>

            <div>
              <h4 className={`font-medium mb-3 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>Transferred Items</h4>
              <div className="overflow-x-auto">
                <table className={`min-w-full divide-y border rounded-lg ${darkMode ? 'divide-gray-600 border-gray-600' : 'divide-gray-200 border-gray-200'}`}>
                  <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Tile</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Fresh Stock</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Corner Damaged</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Full Damaged</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-600' : 'bg-white divide-gray-200'}`}>
                    {selectedTransfer.items?.map((item, index) => (
                      <tr key={index} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                        <td className="px-4 py-3">
                          <div>
                            <div className={`font-medium text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{item.tileId?.tilesName}</div>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.tileId?.id}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="text-green-600">
                            {item.freshStockBox} Boxes, {item.freshStockPieces} Pieces
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="text-yellow-600">
                            {item.cornerDamagedBox || 0} Boxes, {item.cornerDamagedPieces || 0} Pieces
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="text-red-600">
                            {item.fullDamagedBox || 0} Boxes, {item.fullDamagedPieces || 0} Pieces
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium">
                          {(item.freshStockBox + (item.cornerDamagedBox || 0) + (item.fullDamagedBox || 0))} Boxes
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className={`px-6 py-4 border-t ${darkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
            <div className="flex justify-end">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  );

  return (
    <div className={`min-h-screen p-6 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="w-full mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>Tiles Location Transfer</h1>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Transfer tiles between locations efficiently</p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className="flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('entry')}
              className={`${
                activeTab === 'entry'
                  ? 'border-blue-500 text-blue-600'
                  : darkMode
                    ? 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
            >
              <FaExchangeAlt className="inline mr-2" />
              New Transfer Entry
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : darkMode
                    ? 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
            >
              <FaHistory className="inline mr-2" />
              Transfer History
            </button>
          </nav>
        </div>

        {/* Entry Tab */}
        {activeTab === 'entry' && (
          <div className={`rounded-lg shadow-sm border p-6 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <form onSubmit={handleSubmit}>
              {/* Basic Information */}
              <div className="mb-8">
                <h3 className={`text-lg font-semibold mb-4 flex items-center ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                  <FaMapMarkerAlt className="mr-2 text-blue-600" />
                  Transfer Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <FaCalendarAlt className="inline mr-1" />
                      Transfer Date *
                    </label>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                      required
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <FaMapMarkerAlt className="inline mr-1" />
                      From Location *
                    </label>
                    <Select
                      value={selectedFromLocation}
                      onChange={setSelectedFromLocation}
                      options={locations.map(location => ({
                        value: location._id,
                        label: location.locationName || location.name
                      }))}
                      placeholder="Select from location..."
                      className="react-select-container"
                      classNamePrefix="react-select"
                      isSearchable
                      required
                      styles={{
                        control: (base) => ({
                          ...base,
                          backgroundColor: darkMode ? '#374151' : 'white',
                          borderColor: darkMode ? '#4b5563' : '#d1d5db',
                          '&:hover': { borderColor: darkMode ? '#6b7280' : '#9ca3af' }
                        }),
                        menu: (base) => ({ ...base, backgroundColor: darkMode ? '#374151' : 'white' }),
                        option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? (darkMode ? '#4b5563' : '#e5e7eb') : 'transparent', color: darkMode ? '#f3f4f6' : '#111827' }),
                        singleValue: (base) => ({ ...base, color: darkMode ? '#f3f4f6' : '#111827' }),
                        input: (base) => ({ ...base, color: darkMode ? '#f3f4f6' : '#111827' }),
                        placeholder: (base) => ({ ...base, color: darkMode ? '#9ca3af' : '#6b7280' })
                      }}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <FaMapMarkerAlt className="inline mr-1" />
                      To Location *
                    </label>
                    <Select
                      value={selectedToLocation}
                      onChange={setSelectedToLocation}
                      options={locations
                        .filter(location => location._id !== selectedFromLocation?.value)
                        .map(location => ({
                          value: location._id,
                          label: location.locationName || location.name
                        }))}
                      placeholder="Select to location..."
                      className="react-select-container"
                      classNamePrefix="react-select"
                      isSearchable
                      isDisabled={!selectedFromLocation}
                      required
                      styles={{
                        control: (base) => ({
                          ...base,
                          backgroundColor: darkMode ? '#374151' : 'white',
                          borderColor: darkMode ? '#4b5563' : '#d1d5db',
                          '&:hover': { borderColor: darkMode ? '#6b7280' : '#9ca3af' }
                        }),
                        menu: (base) => ({ ...base, backgroundColor: darkMode ? '#374151' : 'white' }),
                        option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? (darkMode ? '#4b5563' : '#e5e7eb') : 'transparent', color: darkMode ? '#f3f4f6' : '#111827' }),
                        singleValue: (base) => ({ ...base, color: darkMode ? '#f3f4f6' : '#111827' }),
                        input: (base) => ({ ...base, color: darkMode ? '#f3f4f6' : '#111827' }),
                        placeholder: (base) => ({ ...base, color: darkMode ? '#9ca3af' : '#6b7280' })
                      }}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <FaStickyNote className="inline mr-1" />
                      Remarks
                    </label>
                    <input
                      type="text"
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Transfer remarks..."
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                  </div>
                </div>
              </div>

              {/* Items Section */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-lg font-semibold flex items-center ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                    <FaBox className="mr-2 text-blue-600" />
                    Transfer Items
                  </h3>
                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center"
                  >
                    <FaPlus className="mr-1" />
                    Add Item
                  </button>
                </div>

                <div className="space-y-4">
                  {items.map((item, index) => (
                    <div key={index} className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex justify-between items-start mb-4">
                        <h4 className={`font-medium ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>Item {index + 1}</h4>
                        {items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            className="text-red-600 hover:text-red-800 transition-colors duration-200"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
                        {/* Tile Selection */}
                        <div className="lg:col-span-1">
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Tile *</label>
                          <button
                            type="button"
                            onClick={() => openTileSelectionModal(index)}
                            className={`w-full px-3 py-2 border rounded-lg hover:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                          >
                            {item.tileName || 'Select tile...'}
                          </button>
                        </div>

                        {/* Fresh Stock inputs */}
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Fresh (Box)
                          </label>
                          <input
                            ref={el => freshStockBoxRefs.current[index] = el}
                            type="number"
                            min="0"
                            max={item.availableStock?.freshStockBox || 0}
                            value={item.freshStockBox}
                            onChange={(e) => handleStockChange(index, 'freshStockBox', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'freshStockBox')}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                            disabled={!item.tileId}
                          />
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Fresh (Pcs)
                          </label>
                          <input
                            ref={el => freshStockPiecesRefs.current[index] = el}
                            type="number"
                            min="0"
                            max={item.availableStock?.freshStockPieces || 0}
                            value={item.freshStockPieces}
                            onChange={(e) => handleStockChange(index, 'freshStockPieces', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'freshStockPieces')}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                            disabled={!item.tileId}
                          />
                        </div>

                        {/* Corner Damaged inputs */}
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Corner Dmg (Box)
                          </label>
                          <input
                            ref={el => cornerDamagedBoxRefs.current[index] = el}
                            type="number"
                            min="0"
                            max={item.availableStock?.cornerDamagedBox || 0}
                            value={item.cornerDamagedBox}
                            onChange={(e) => handleStockChange(index, 'cornerDamagedBox', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'cornerDamagedBox')}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                            disabled={!item.tileId}
                          />
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Corner Dmg (Pcs)
                          </label>
                          <input
                            ref={el => cornerDamagedPiecesRefs.current[index] = el}
                            type="number"
                            min="0"
                            max={item.availableStock?.cornerDamagedPieces || 0}
                            value={item.cornerDamagedPieces}
                            onChange={(e) => handleStockChange(index, 'cornerDamagedPieces', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'cornerDamagedPieces')}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                            disabled={!item.tileId}
                          />
                        </div>

                        {/* Full Damaged inputs */}
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Full Dmg (Box)
                          </label>
                          <input
                            ref={el => fullDamagedBoxRefs.current[index] = el}
                            type="number"
                            min="0"
                            max={item.availableStock?.fullDamagedBox || 0}
                            value={item.fullDamagedBox}
                            onChange={(e) => handleStockChange(index, 'fullDamagedBox', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'fullDamagedBox')}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                            disabled={!item.tileId}
                          />
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Full Dmg (Pcs)
                          </label>
                          <input
                            ref={el => fullDamagedPiecesRefs.current[index] = el}
                            type="number"
                            min="0"
                            max={item.availableStock?.fullDamagedPieces || 0}
                            value={item.fullDamagedPieces}
                            onChange={(e) => handleStockChange(index, 'fullDamagedPieces', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'fullDamagedPieces')}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                            disabled={!item.tileId}
                          />
                        </div>
                      </div>

                      {/* Available Stock Display */}
                      {item.tileId && (
                        <div className={`mt-4 p-3 rounded-lg ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                          <div className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                            <span className="font-medium">Available Stock:</span>
                            <div className="grid grid-cols-3 gap-4 mt-2">
                              <div>
                                <span className="text-green-600">Fresh: {item.availableStock?.freshStockBox || 0} Box, {item.availableStock?.freshStockPieces || 0} Pcs</span>
                              </div>
                              <div>
                                <span className="text-yellow-600">Corner Dmg: {item.availableStock?.cornerDamagedBox || 0} Box, {item.availableStock?.cornerDamagedPieces || 0} Pcs</span>
                              </div>
                              <div>
                                <span className="text-red-600">Full Dmg: {item.availableStock?.fullDamagedBox || 0} Box, {item.availableStock?.fullDamagedPieces || 0} Pcs</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors duration-200 flex items-center"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating Transfer...
                    </>
                  ) : (
                    <>
                      <FaSave className="mr-2" />
                      Create Transfer
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className={`rounded-lg shadow-sm border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className={`px-6 py-4 border-b ${darkMode ? 'bg-gradient-to-r from-gray-700 to-gray-600 border-gray-600' : 'bg-gradient-to-r from-gray-100 to-gray-200 border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FaFileExport className={`w-6 h-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                <div>
                  <h2 className={`text-xl font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>Transfer History</h2>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Recent location transfers and tracking</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className={`px-4 py-2 rounded-lg shadow-sm ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                  <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Total: <span className="text-blue-600 font-bold">{totalHistory}</span> transfers
                  </span>
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    showFilters 
                      ? 'bg-blue-600 text-white shadow-md' 
                      : darkMode
                        ? 'border border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <FaFilter className="w-4 h-4" />
                  {showFilters ? 'Hide Filters' : 'Show Filters'}
                </button>
              </div>
            </div>

            {/* Enhanced Filters */}
            {showFilters && (
              <div className={`border-t p-6 ${darkMode ? 'bg-gradient-to-r from-gray-700 to-gray-600 border-gray-600' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-gray-200'}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div>
                    <label className={`block text-sm font-medium mb-2 flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <FaMapMarkerAlt className="mr-2 text-blue-500" />
                      From Location
                    </label>
                    <Select
                      value={historyFilters.fromLocation ? { value: historyFilters.fromLocation, label: locations.find(l => l._id === historyFilters.fromLocation)?.locationName } : null}
                      onChange={(option) => handleHistoryFilterChange('fromLocation', option?.value || null)}
                      options={locations.map(location => ({
                        value: location._id,
                        label: location.locationName || location.name
                      }))}
                      placeholder="All locations"
                      isClearable
                      className="text-sm"
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '44px',
                          border: darkMode ? '1px solid #4b5563' : '1px solid #d1d5db',
                          borderRadius: '8px',
                          backgroundColor: darkMode ? '#374151' : 'white',
                          '&:hover': { borderColor: '#3b82f6' }
                        }),
                        menu: (base) => ({ ...base, backgroundColor: darkMode ? '#374151' : 'white' }),
                        option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? (darkMode ? '#4b5563' : '#e5e7eb') : 'transparent', color: darkMode ? '#f3f4f6' : '#111827' }),
                        singleValue: (base) => ({ ...base, color: darkMode ? '#f3f4f6' : '#111827' }),
                        input: (base) => ({ ...base, color: darkMode ? '#f3f4f6' : '#111827' }),
                        placeholder: (base) => ({ ...base, color: darkMode ? '#9ca3af' : '#6b7280' })
                      }}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <FaMapMarkerAlt className="mr-2 text-green-500" />
                      To Location
                    </label>
                    <Select
                      value={historyFilters.toLocation ? { value: historyFilters.toLocation, label: locations.find(l => l._id === historyFilters.toLocation)?.locationName } : null}
                      onChange={(option) => handleHistoryFilterChange('toLocation', option?.value || null)}
                      options={locations.map(location => ({
                        value: location._id,
                        label: location.locationName || location.name
                      }))}
                      placeholder="All locations"
                      isClearable
                      className="text-sm"
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '44px',
                          border: darkMode ? '1px solid #4b5563' : '1px solid #d1d5db',
                          borderRadius: '8px',
                          backgroundColor: darkMode ? '#374151' : 'white',
                          '&:hover': { borderColor: '#3b82f6' }
                        }),
                        menu: (base) => ({ ...base, backgroundColor: darkMode ? '#374151' : 'white' }),
                        option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? (darkMode ? '#4b5563' : '#e5e7eb') : 'transparent', color: darkMode ? '#f3f4f6' : '#111827' }),
                        singleValue: (base) => ({ ...base, color: darkMode ? '#f3f4f6' : '#111827' }),
                        input: (base) => ({ ...base, color: darkMode ? '#f3f4f6' : '#111827' }),
                        placeholder: (base) => ({ ...base, color: darkMode ? '#9ca3af' : '#6b7280' })
                      }}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <FaCalendarAlt className="mr-2 text-purple-500" />
                      Start Date
                    </label>
                    <input
                      ref={filterDateStartRef}
                      type="date"
                      value={historyFilters.startDate || ''}
                      onChange={(e) => handleHistoryFilterChange('startDate', e.target.value || null)}
                      onKeyDown={(e) => handleFilterKeyDown(e, 'startDate')}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm transition-all ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <FaCalendarAlt className="mr-2 text-orange-500" />
                      End Date
                    </label>
                    <input
                      ref={filterDateEndRef}
                      type="date"
                      value={historyFilters.endDate || ''}
                      onChange={(e) => handleHistoryFilterChange('endDate', e.target.value || null)}
                      onKeyDown={(e) => handleFilterKeyDown(e, 'endDate')}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm transition-all ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'}`}
                    />
                  </div>
                </div>
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={resetHistoryFilters}
                    className={`px-6 py-3 border rounded-lg transition-all flex items-center gap-2 ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-white hover:shadow-md'}`}
                  >
                    <FaTimes className="w-4 h-4" />
                    Reset Filters
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-600' : 'divide-gray-200'}`}>
              <thead className={darkMode ? 'bg-gradient-to-r from-gray-700 to-gray-600' : 'bg-gradient-to-r from-gray-100 to-gray-200'}>
                <tr>
                  <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <div className="flex items-center gap-2">
                      <FaFileExport className="w-4 h-4" />
                      Transfer ID
                    </div>
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <div className="flex items-center gap-2">
                      <FaCalendarAlt className="w-4 h-4" />
                      Date
                    </div>
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <div className="flex items-center gap-2">
                      <FaExchangeAlt className="w-4 h-4" />
                      Transfer Route
                    </div>
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <div className="flex items-center gap-2">
                      <FaBox className="w-4 h-4" />
                      Items
                    </div>
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <div className="flex items-center gap-2">
                      <FaBox className="w-4 h-4" />
                      Total Boxes
                    </div>
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <div className="flex items-center gap-2">
                      <FaEye className="w-4 h-4" />
                      Actions
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-600' : 'bg-white divide-gray-200'}`}>
                {historyLoading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                        <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading transfer history...</span>
                        <span className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Please wait while we fetch the data</span>
                      </div>
                    </td>
                  </tr>
                ) : transferHistory.length > 0 ? (
                  transferHistory.map((transfer, index) => (
                    <tr key={transfer._id} className={`transition-colors ${index % 2 === 0 ? (darkMode ? 'bg-gray-800' : 'bg-white') : (darkMode ? 'bg-gray-750' : 'bg-gray-50')} ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-blue-50'}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`px-3 py-1 rounded-full text-sm font-semibold ${darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                            {transfer.transferId}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                          {formatDate(transfer.transferDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 rounded-lg text-xs font-medium ${darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                            {transfer.fromLocation?.locationName || transfer.fromLocation?.name || "Unknown"}
                          </span>
                          <FaArrowRight className={`w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                          <span className={`px-3 py-1 rounded-lg text-xs font-medium ${darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800'}`}>
                            {transfer.toLocation?.locationName || transfer.toLocation?.name || "Unknown"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'}`}>
                            {transfer.items?.length || 0}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${darkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-800'}`}>
                            {calculateTotalStock(transfer.items || [])}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => {
                            setSelectedTransfer(transfer);
                            setShowDetailsModal(true);
                          }}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all flex items-center gap-2 text-sm font-medium shadow-sm hover:shadow-md"
                        >
                          <FaEye className="w-4 h-4" />
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <FaFileExport className={`w-12 h-12 mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                        <span className={`font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No transfers found</span>
                        <span className={`text-sm mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Try adjusting your filters or create a new transfer</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Enhanced Pagination */}
          {totalHistory > historyLimit && (
            <div className={`px-6 py-4 border-t ${darkMode ? 'bg-gradient-to-r from-gray-700 to-gray-600 border-gray-600' : 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200'}`}>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <span className="font-medium">
                    Showing {((historyPage - 1) * historyLimit) + 1} to {Math.min(historyPage * historyLimit, totalHistory)} of {totalHistory} transfers
                  </span>
                </div>
                <div className="flex items-center justify-center md:justify-end space-x-2">
                  <button
                    onClick={() => setHistoryPage(Math.max(1, historyPage - 1))}
                    disabled={historyPage === 1}
                    className={`px-4 py-2 border rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 hover:bg-white hover:shadow-sm'}`}
                  >
                    <FaArrowRight className="w-3 h-3 rotate-180" />
                    Previous
                  </button>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Page</span>
                    <span className="bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium min-w-[3rem] text-center">
                      {historyPage}
                    </span>
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>of {Math.ceil(totalHistory / historyLimit)}</span>
                  </div>
                  <button
                    onClick={() => setHistoryPage(historyPage + 1)}
                    disabled={historyPage >= Math.ceil(totalHistory / historyLimit)}
                    className={`px-4 py-2 border rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 hover:bg-white hover:shadow-sm'}`}
                  >
                    Next
                    <FaArrowRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        )}

      </div>

      {/* Modals */}
      <TileSelectionModal
        isOpen={showTilesModal}
        onClose={() => { setShowTilesModal(false); setCurrentItemIndex(null); }}
        onSelect={(tile) => handleTileSelect(tile)}
        darkMode={darkMode}
        userId={user?.user?._id || user?.user?.id}
        locationId={selectedFromLocation?.value}
        showNetStock={false}
      />
      <DetailsModal />
    </div>
  );
};

export default TilesLocationTransfer;